import { Link } from 'react-router-dom';
import { signupEntryPath, createActivityMarketingPath } from '@utils/authEntryPaths';
import { Sparkles, Users, MapPin, MessageCircle, Shield, Star, Sun, Moon, ArrowRight, Zap } from 'lucide-react';
import Logo from '@components/ui/Logo';
import { useTheme } from '@context/ThemeContext';

/** `create` uses viewport-aware marketing path; `feed` → Explore. */
const features = [
  {
    icon: Sparkles,
    title: 'Create',
    description: 'Post a vibe you want to do nearby — from pickup games to study sessions.',
    gradient: 'from-brand-500 to-blue-600',
    glow: 'shadow-brand-500/20',
    stepLink: 'create',
  },
  {
    icon: Users,
    title: 'Match',
    description: 'Instantly find people who share the same interests and are close to you.',
    gradient: 'from-violet-500 to-purple-600',
    glow: 'shadow-violet-500/20',
    stepLink: 'feed',
  },
  {
    icon: MapPin,
    title: 'Meet',
    description: 'Join vibes happening around you. Show up, have fun, repeat.',
    gradient: 'from-emerald-500 to-teal-600',
    glow: 'shadow-emerald-500/20',
    stepLink: 'feed',
  },
];

/** Maps hero chips → Explore (`/feed`) with API category + title search. */
const categories = [
  { emoji: '🏸', name: 'Badminton', feedCategory: 'sports', feedQuery: 'badminton' },
  { emoji: '🏃', name: 'Running', feedCategory: 'fitness', feedQuery: 'running' },
  { emoji: '🧘', name: 'Yoga', feedCategory: 'fitness', feedQuery: 'yoga' },
  { emoji: '🍳', name: 'Cooking', feedCategory: 'food', feedQuery: 'cooking' },
  { emoji: '🎬', name: 'Movies', feedCategory: 'social', feedQuery: 'movie' },
  { emoji: '📚', name: 'Study Groups', feedCategory: 'learning', feedQuery: 'study' },
  { emoji: '🎮', name: 'Gaming', feedCategory: 'gaming', feedQuery: 'gaming' },
  { emoji: '🚴', name: 'Cycling', feedCategory: 'sports', feedQuery: 'cycling' },
  { emoji: '💪', name: 'Gym', feedCategory: 'fitness', feedQuery: 'gym' },
  { emoji: '🏊', name: 'Swimming', feedCategory: 'sports', feedQuery: 'swimming' },
];

const feedHref = ({ feedCategory, feedQuery }) => {
  const params = new URLSearchParams();
  if (feedCategory) params.set('category', feedCategory);
  if (feedQuery) params.set('q', feedQuery);
  const qs = params.toString();
  return qs ? `/feed?${qs}` : '/feed';
};

const stats = [
  { value: '10K+', label: 'Active Users' },
  { value: '5K+', label: 'Vibes Created' },
  { value: '50+', label: 'Cities' },
];

const LandingPage = () => {
  const { dark, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-white dark:bg-surface-950 transition-colors duration-500">
      {/* Navbar — min-height + safe area so logo/CTA never clip; CTA is compact on narrow widths */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header pt-[env(safe-area-inset-top,0px)]">
        <div className="mx-auto flex min-h-[3.5rem] max-w-6xl items-center justify-between gap-2 px-3 sm:min-h-[4.5rem] sm:gap-3 sm:px-6 lg:px-8">
          <div className="min-w-0 shrink">
            <Logo size="md" to="/" />
          </div>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
            <button type="button" onClick={toggleTheme} className="theme-toggle shrink-0" aria-label="Toggle dark mode">
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Link to="/login" className="btn-ghost hidden sm:inline-flex">
              Log in
            </Link>
            <Link
              to={signupEntryPath()}
              className="inline-flex h-9 shrink-0 items-center justify-center whitespace-nowrap rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 px-3 text-xs font-bold text-white shadow-md shadow-brand-500/25 transition hover:brightness-110 active:scale-[0.98] sm:h-10 sm:px-5 sm:text-sm"
            >
              Get started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden px-4 sm:px-6 lg:px-8 pt-[calc(6.5rem+env(safe-area-inset-top,0px))] sm:pt-[calc(7.5rem+env(safe-area-inset-top,0px))] lg:pt-48 pb-16 sm:pb-24 lg:pb-32">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-10 left-[10%] h-72 w-72 sm:h-96 sm:w-96 rounded-full bg-brand-500/8 blur-3xl dark:bg-brand-500/5" />
          <div className="absolute bottom-10 right-[10%] h-72 w-72 sm:h-96 sm:w-96 rounded-full bg-violet-500/8 blur-3xl dark:bg-violet-500/5" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-56 w-56 sm:h-80 sm:w-80 rounded-full bg-emerald-500/5 blur-3xl" />
        </div>

        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-6 inline-flex max-w-full flex-wrap items-center justify-center gap-2 rounded-full bg-brand-50 px-4 py-2 text-xs font-semibold text-brand-600 shadow-sm dark:bg-brand-500/10 dark:text-brand-400 sm:px-5 sm:py-2.5 sm:text-sm">
            <Zap size={14} className="shrink-0 text-brand-500" />
            <span className="text-center leading-snug">Find your next adventure nearby</span>
          </div>

          <h1 className="mb-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.08] tracking-tight text-surface-900 dark:text-white">
            Do more,{' '}
            <span className="gradient-text">
              together.
            </span>
          </h1>

          <p className="mb-10 text-base sm:text-lg lg:text-xl text-surface-500 dark:text-surface-400 max-w-2xl mx-auto leading-relaxed">
            Find people nearby who want to do the same vibes as you — sports, fitness,
            learning, or just hanging out.
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Link to="/feed" className="btn-primary !text-base !px-8 !py-4 gap-2">
              Vibes Near You
              <ArrowRight size={18} />
            </Link>
            <Link to={createActivityMarketingPath()} className="btn-secondary !text-base !px-8 !py-4">
              Start a Vibe
            </Link>
          </div>

          {/* Stats — grid keeps three columns inside the viewport on small phones */}
          <div className="mx-auto mt-16 grid w-full max-w-md grid-cols-3 gap-2 px-0.5 sm:mt-20 sm:max-w-none sm:gap-8 md:gap-12">
            {stats.map((s) => (
              <div key={s.label} className="min-w-0 px-0.5 text-center sm:px-2">
                <div className="text-2xl font-extrabold leading-none gradient-text sm:text-3xl md:text-4xl">{s.value}</div>
                <div className="mt-1.5 text-[10px] font-medium leading-snug text-surface-400 sm:text-xs md:text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="mx-auto max-w-4xl">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.name}
                to={feedHref(cat)}
                className="flex items-center gap-2 rounded-2xl border border-surface-200/80 bg-white px-5 py-3 text-sm font-medium text-surface-700 shadow-sm transition-all duration-300 hover:border-brand-300 hover:shadow-md hover:-translate-y-0.5 dark:border-surface-700/50 dark:bg-surface-800/50 dark:text-surface-300 dark:hover:border-brand-500/30 cursor-pointer"
              >
                <span className="text-lg">{cat.emoji}</span>
                <span>{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 sm:py-28 bg-surface-50/80 dark:bg-surface-900/30">
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-600 dark:bg-violet-500/10 dark:text-violet-400">
              Simple & Easy
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-surface-900 dark:text-white">
              How it works
            </h2>
          </div>

          <div className="grid gap-6 sm:grid-cols-3">
            {features.map((f, i) => {
              const to = f.stepLink === 'create' ? createActivityMarketingPath() : '/feed';
              return (
                <Link
                  key={f.title}
                  to={to}
                  className="group relative block rounded-3xl border border-surface-200/60 bg-white p-8 sm:p-10 text-center transition-all duration-500 hover:shadow-xl hover:-translate-y-1 dark:border-surface-800 dark:bg-surface-900/80 dark:hover:border-surface-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-50 dark:focus-visible:ring-offset-surface-900"
                >
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-brand-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 dark:from-brand-500/10" />
                  <div className="relative">
                    <div className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${f.gradient} shadow-lg ${f.glow}`}>
                      <f.icon size={28} className="text-white" />
                    </div>
                    <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-surface-400">Step {i + 1}</div>
                    <h3 className="mb-3 text-xl sm:text-2xl font-bold text-surface-900 dark:text-white">{f.title}</h3>
                    <p className="text-sm sm:text-base text-surface-500 dark:text-surface-400 leading-relaxed">{f.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust / Safety */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400">
              <Shield size={12} /> Your Safety Matters
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-surface-900 dark:text-white">
              Built with safety in mind
            </h2>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {[
              { icon: Shield, title: 'Verified profiles', desc: 'Face verification unlocks unlimited hosting; everyone can try hosting a few vibes first.', color: 'text-brand-500 bg-brand-50 dark:bg-brand-500/10' },
              { icon: MessageCircle, title: 'Secure group chat', desc: 'Chat is only available for people who joined the vibe — no spam.', color: 'text-violet-500 bg-violet-50 dark:bg-violet-500/10' },
              { icon: Star, title: 'Ratings & reviews', desc: 'Rate others after vibes to build community trust and accountability.', color: 'text-amber-500 bg-amber-50 dark:bg-amber-500/10' },
              { icon: Users, title: 'Report system', desc: 'Easily report inappropriate behavior to keep the platform safe for everyone.', color: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-500/10' },
            ].map((item) => (
              <div key={item.title} className="flex gap-5 rounded-2xl border border-surface-200/60 bg-white p-6 sm:p-7 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 dark:border-surface-800 dark:bg-surface-900/80">
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${item.color}`}>
                  <item.icon size={22} />
                </div>
                <div>
                  <h4 className="font-bold text-lg text-surface-900 dark:text-white mb-1">{item.title}</h4>
                  <p className="text-sm text-surface-500 dark:text-surface-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="mx-auto max-w-3xl relative">
          <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-brand-500 to-violet-600 blur-2xl opacity-20 dark:opacity-30" />
          <div className="relative rounded-[2rem] bg-gradient-to-br from-brand-500 via-brand-600 to-violet-600 p-10 sm:p-16 text-center shadow-2xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1.5 text-xs font-semibold text-white/90">
              <Sparkles size={12} /> It&apos;s free to get started
            </div>
            <h2 className="mb-4 text-3xl sm:text-4xl font-extrabold text-white">Ready to vibe?</h2>
            <p className="mb-10 text-base sm:text-lg text-white/70 max-w-lg mx-auto">
              Join thousands of people finding vibe partners near them. Your next adventure is one tap away.
            </p>
            <Link
              to={signupEntryPath()}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-8 py-4 text-base font-bold text-brand-600 shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 active:scale-[0.97]"
            >
              Get Started Free
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-surface-100 dark:border-surface-800 px-4 sm:px-6 lg:px-8 py-10">
        <div className="mx-auto flex max-w-6xl flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Logo size="md" to="/" />
            <p className="text-sm text-surface-400">&copy; 2026 Yaarvi. All rights reserved.</p>
          </div>
          <div className="flex items-center gap-5 text-xs text-surface-400 dark:text-surface-500">
            <Link to="/privacy" className="hover:text-brand-500 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-brand-500 transition-colors">Terms of Service</Link>
            <Link to="/delete-account" className="hover:text-brand-500 transition-colors">Delete Account</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
